import {
	coordKey,
	getDirection,
	getNeighbors,
	type HexDirection,
	parseCoordKey,
} from "../../hex-path/hex-grid";
import type { HexCoord } from "../../hex-path/types";
import type { MapLayout, ModuleMapGraph, PathCell } from "../map-graph-types";
import type { HexagonLayoutMetrics } from "./hexagon-layout-metrics";
import { hexCellCenter } from "./hexagon-layout-metrics";

export function normalizeBranchCorridorId(
	branchCorridorId: string | null | undefined,
): string | null {
	if (branchCorridorId == null) {
		return null;
	}
	const trimmed = branchCorridorId.trim();
	return trimmed.length > 0 ? trimmed : null;
}

export function normalizeMapPlayerPathPosition(
	position: MapPlayerPathPosition,
): MapPlayerPathPosition {
	return {
		pathProgress: position.pathProgress,
		branchCorridorId: normalizeBranchCorridorId(position.branchCorridorId),
		pathNodeId: normalizePathNodeId(position.pathNodeId),
	};
}

/** Merge layout corridor ids with per-cell ids from path assignment. */
export function buildBranchCorridorIdByCoordKeyForLayout(
	layout: Pick<
		MapLayout,
		"pathCells" | "branchCorridorIdByCoordKey" | "pathCoordSet"
	>,
): Map<string, string | null> {
	const merged = new Map<string, string | null>();
	for (const key of layout.pathCoordSet) {
		merged.set(
			key,
			normalizeBranchCorridorId(
				layout.branchCorridorIdByCoordKey.get(key),
			),
		);
	}
	for (const cell of layout.pathCells) {
		if (cell.branchCorridorId !== undefined) {
			merged.set(
				coordKey(cell.row, cell.col),
				normalizeBranchCorridorId(cell.branchCorridorId),
			);
		}
	}
	return merged;
}

function pathPositionLookupKey(
	pathProgress: number,
	branchCorridorId: string | null,
): string {
	return `${pathProgress}:${branchCorridorId ?? "__main__"}`;
}

export function buildPathPositionLookup(
	pathProgressByCoordKey: Map<string, number>,
	branchCorridorIdByCoordKey: Map<string, string | null>,
): Map<string, HexCoord> {
	const lookup = new Map<string, HexCoord>();
	for (const [key, pathProgress] of pathProgressByCoordKey) {
		const branchCorridorId = normalizeBranchCorridorId(
			branchCorridorIdByCoordKey.get(key),
		);
		const lookupKey = pathPositionLookupKey(pathProgress, branchCorridorId);
		if (!lookup.has(lookupKey)) {
			lookup.set(lookupKey, parseCoordKey(key));
		}
	}
	return lookup;
}

export interface PathCharacterPlayerIcons {
	upRight: string;
	right: string;
	downRight: string;
	upLeft: string;
	left: string;
	downLeft: string;
}

export const DEFAULT_CHARACTER_MOVEMENT_MS_PER_TILE = 300;
/** @deprecated Use DEFAULT_CHARACTER_MOVEMENT_MS_PER_TILE */
export const DEFAULT_CHARACTER_MOVEMENT_DURATION_MS =
	DEFAULT_CHARACTER_MOVEMENT_MS_PER_TILE;

/** Seed-independent position along the visible path graph. */
export interface MapPlayerPathPosition {
	/** Shortest-walk steps from the map start through connected path tiles. */
	pathProgress: number;
	/** Null for main-spine tiles; branch corridor id for optional/nested branch tiles. */
	branchCorridorId: string | null;
	/** Graph node id when the tile maps to a module path node (exact restore across layout seeds). */
	pathNodeId?: string | null;
}

function normalizePathNodeId(
	pathNodeId: string | null | undefined,
): string | null {
	if (pathNodeId == null) {
		return null;
	}
	const trimmed = pathNodeId.trim();
	return trimmed.length > 0 ? trimmed : null;
}

const MAP_STOP_PATH_NODE_PREFIX = "sq:map:stop:";

/** Module stop id when the saved path node is a stop tile; otherwise null. */
export function resolveStopIdFromPathNodeId(
	pathNodeId: string | null | undefined,
): string | null {
	const normalized = normalizePathNodeId(pathNodeId);
	if (!normalized?.startsWith(MAP_STOP_PATH_NODE_PREFIX)) {
		return null;
	}
	return normalized.slice(MAP_STOP_PATH_NODE_PREFIX.length);
}

/**
 * Path tiles that should render as completed (green): each completed stop's tile plus
 * spacer tiles on the graph walk since the previous stop.
 */
export function buildCompletedPathCoordKeys(
	graph: ModuleMapGraph,
	layout: Pick<MapLayout, "pathCoordSet" | "nodeById">,
	completedStopIds: Set<string>,
): Set<string> {
	const completed = new Set<string>();
	let segmentCoordKeys: string[] = [];

	for (const node of graph.nodes) {
		const coord = layout.nodeById.get(node.id);
		if (coord) {
			const key = coordKey(coord.row, coord.col);
			if (layout.pathCoordSet.has(key)) {
				segmentCoordKeys.push(key);
			}
		}

		if (node.kind === "content") {
			if (completedStopIds.has(node.stopId)) {
				for (const key of segmentCoordKeys) {
					completed.add(key);
				}
			}
			segmentCoordKeys = [];
		}
	}

	return completed;
}

/**
 * Path tiles that should render as locked: each locked stop's tile plus spacer tiles
 * on the graph walk since the previous stop.
 */
export function buildLockedPathCoordKeys(
	graph: ModuleMapGraph,
	layout: Pick<MapLayout, "pathCoordSet" | "nodeById">,
	unlockedStopIds: Set<string>,
): Set<string> {
	const locked = new Set<string>();
	let segmentCoordKeys: string[] = [];

	for (const node of graph.nodes) {
		const coord = layout.nodeById.get(node.id);
		if (coord) {
			const key = coordKey(coord.row, coord.col);
			if (layout.pathCoordSet.has(key)) {
				segmentCoordKeys.push(key);
			}
		}

		if (node.kind === "content") {
			if (!unlockedStopIds.has(node.stopId)) {
				for (const key of segmentCoordKeys) {
					locked.add(key);
				}
			}
			segmentCoordKeys = [];
		}
	}

	return locked;
}

/** Shortest walk distance from the map start through connected path tiles. */
export function buildPathProgressByCoordKey(
	pathCoordSet: Set<string>,
	startCell: HexCoord,
): Map<string, number> {
	const progressByCoord = new Map<string, number>();
	const startKey = coordKey(startCell.row, startCell.col);
	if (!pathCoordSet.has(startKey)) {
		return progressByCoord;
	}

	const queue: HexCoord[] = [startCell];
	progressByCoord.set(startKey, 0);

	while (queue.length > 0) {
		const current = queue.shift();
		if (!current) {
			continue;
		}
		const currentKey = coordKey(current.row, current.col);
		const currentProgress = progressByCoord.get(currentKey) ?? 0;

		for (const neighbor of getNeighbors(current.row, current.col)) {
			const neighborKey = coordKey(neighbor.row, neighbor.col);
			if (
				!pathCoordSet.has(neighborKey) ||
				progressByCoord.has(neighborKey)
			) {
				continue;
			}
			progressByCoord.set(neighborKey, currentProgress + 1);
			queue.push(neighbor);
		}
	}

	return progressByCoord;
}

/**
 * Stable path progress based on assigned `PathCell.pathIndex` (graph order).
 * This avoids ambiguity when the visible path graph has cycles (e.g. branch corridors that rejoin),
 * where shortest-walk distances can collide for multiple tiles.
 */
export function buildPathIndexProgressByCoordKeyFromLayout(
	layout: Pick<MapLayout, "pathCells" | "pathCoordSet">,
): Map<string, number> {
	const progressByCoord = new Map<string, number>();
	for (const cell of layout.pathCells) {
		const key = coordKey(cell.row, cell.col);
		if (!layout.pathCoordSet.has(key)) {
			continue;
		}
		progressByCoord.set(key, Math.max(0, Math.floor(cell.pathIndex ?? 0)));
	}
	return progressByCoord;
}

export function resolvePathPositionForCoord(
	coord: HexCoord,
	pathProgressByCoordKey: Map<string, number>,
	branchCorridorIdByCoordKey: Map<string, string | null>,
	pathCellByCoordKey?: Map<string, Pick<PathCell, "nodeId">>,
): MapPlayerPathPosition | null {
	const key = coordKey(coord.row, coord.col);
	const pathProgress = pathProgressByCoordKey.get(key);
	if (pathProgress == null) {
		return null;
	}
	return normalizeMapPlayerPathPosition({
		pathProgress,
		branchCorridorId: branchCorridorIdByCoordKey.get(key) ?? null,
		pathNodeId: pathCellByCoordKey?.get(key)?.nodeId ?? null,
	});
}

/** Persistable position for a path tile (walk progress + graph node id). */
export function resolveMapPlayerPathPositionForPathCell(
	coord: HexCoord,
	pathProgressByCoordKey: Map<string, number>,
	branchCorridorIdByCoordKey: Map<string, string | null>,
	pathCell: Pick<PathCell, "nodeId"> | null | undefined,
): MapPlayerPathPosition | null {
	const key = coordKey(coord.row, coord.col);
	const pathProgress = pathProgressByCoordKey.get(key);
	if (pathProgress == null) {
		return null;
	}
	return normalizeMapPlayerPathPosition({
		pathProgress,
		branchCorridorId: branchCorridorIdByCoordKey.get(key) ?? null,
		pathNodeId: pathCell?.nodeId ?? null,
	});
}

/** @deprecated Use resolvePathPositionForCoord */
export function resolvePathProgressForCoord(
	coord: HexCoord,
	pathProgressByCoordKey: Map<string, number>,
): number | null {
	return pathProgressByCoordKey.get(coordKey(coord.row, coord.col)) ?? null;
}

/** Resolve the path tile for a persisted seed-independent path position. */
export function resolveCoordFromPathPosition(
	position: MapPlayerPathPosition,
	pathCoordSet: Set<string>,
	startCell: HexCoord,
	branchCorridorIdByCoordKey: Map<string, string | null>,
	pathProgressByCoordKey?: Map<string, number>,
): HexCoord | null {
	const normalizedPosition = normalizeMapPlayerPathPosition(position);
	if (normalizedPosition.pathProgress < 0) {
		return null;
	}
	const progressByCoord =
		pathProgressByCoordKey ??
		buildPathProgressByCoordKey(pathCoordSet, startCell);
	const lookup = buildPathPositionLookup(
		progressByCoord,
		branchCorridorIdByCoordKey,
	);
	return (
		lookup.get(
			pathPositionLookupKey(
				normalizedPosition.pathProgress,
				normalizedPosition.branchCorridorId,
			),
		) ?? null
	);
}

export function areMapPlayerPathPositionsEqual(
	left: MapPlayerPathPosition,
	right: MapPlayerPathPosition | null | undefined,
): boolean {
	if (right == null) {
		return false;
	}
	const normalizedLeft = normalizeMapPlayerPathPosition(left);
	const normalizedRight = normalizeMapPlayerPathPosition(right);
	if (
		normalizedLeft.pathNodeId != null &&
		normalizedRight.pathNodeId != null &&
		normalizedLeft.pathNodeId === normalizedRight.pathNodeId
	) {
		return true;
	}
	return (
		normalizedLeft.pathProgress === normalizedRight.pathProgress &&
		normalizedLeft.branchCorridorId === normalizedRight.branchCorridorId
	);
}

function resolveCoordFromPathNodeId(
	pathNodeId: string | null | undefined,
	layout: MapLayout,
): HexCoord | null {
	const normalizedNodeId = normalizePathNodeId(pathNodeId);
	if (!normalizedNodeId) {
		return null;
	}
	for (const cell of layout.pathCells) {
		if (cell.nodeId !== normalizedNodeId) {
			continue;
		}
		const coord = { row: cell.row, col: cell.col };
		if (layout.pathCoordSet.has(coordKey(coord.row, coord.col))) {
			return coord;
		}
	}
	return null;
}

function resolveStopAnchorCoord(input: {
	playerStopId: string | null | undefined;
	graph: { stopIdToNodeId: Map<string, string> };
	layout: MapLayout;
}): HexCoord | null {
	const stopNodeId = input.playerStopId
		? input.graph.stopIdToNodeId.get(input.playerStopId)
		: null;
	if (!stopNodeId) {
		return null;
	}
	for (const cell of input.layout.pathCells) {
		if (cell.nodeId !== stopNodeId) {
			continue;
		}
		const coord = { row: cell.row, col: cell.col };
		if (input.layout.pathCoordSet.has(coordKey(coord.row, coord.col))) {
			return coord;
		}
	}
	return null;
}

export function resolvePlayerAnchorCoord(input: {
	playerPathPosition: MapPlayerPathPosition | null | undefined;
	playerStopId: string | null | undefined;
	graph: { stopIdToNodeId: Map<string, string> };
	layout: MapLayout;
	pathProgressByCoordKey: Map<string, number>;
	preferStartAnchorWhenNoPathPosition?: boolean;
}): HexCoord | null {
	const branchCorridorIdByCoordKey = buildBranchCorridorIdByCoordKeyForLayout(
		input.layout,
	);

	if (input.playerPathPosition != null) {
		const normalizedPosition = normalizeMapPlayerPathPosition(
			input.playerPathPosition,
		);
		const fromNode = resolveCoordFromPathNodeId(
			normalizedPosition.pathNodeId,
			input.layout,
		);
		// Graph node ids are stable across layout seeds; trust them for restore even when
		// shortest-walk pathProgress at that cell differs (cycles / branch rejoins).
		if (fromNode) {
			return fromNode;
		}
		const fromPosition = resolveCoordFromPathPosition(
			normalizedPosition,
			input.layout.pathCoordSet,
			input.layout.startCell,
			branchCorridorIdByCoordKey,
			input.pathProgressByCoordKey,
		);
		if (
			fromPosition &&
			input.layout.pathCoordSet.has(
				coordKey(fromPosition.row, fromPosition.col),
			)
		) {
			return fromPosition;
		}
		const startKey = coordKey(
			input.layout.startCell.row,
			input.layout.startCell.col,
		);
		if (
			input.preferStartAnchorWhenNoPathPosition &&
			input.layout.pathCoordSet.has(startKey)
		) {
			return input.layout.startCell;
		}
		// Saved position did not resolve on this layout; fall through to stop/start.
	}

	const startKey = coordKey(
		input.layout.startCell.row,
		input.layout.startCell.col,
	);
	if (
		input.preferStartAnchorWhenNoPathPosition &&
		input.layout.pathCoordSet.has(startKey)
	) {
		return input.layout.startCell;
	}

	const stopCoord = resolveStopAnchorCoord(input);
	if (stopCoord) {
		return stopCoord;
	}
	if (input.layout.pathCoordSet.has(startKey)) {
		return input.layout.startCell;
	}
	const firstPathCell = input.layout.pathCells[0];
	if (
		firstPathCell &&
		input.layout.pathCoordSet.has(
			coordKey(firstPathCell.row, firstPathCell.col),
		)
	) {
		return { row: firstPathCell.row, col: firstPathCell.col };
	}
	return null;
}

/** @deprecated Use resolveCoordFromPathPosition */
export function resolveCoordFromPathProgress(
	pathProgress: number,
	pathCoordSet: Set<string>,
	startCell: HexCoord,
	pathProgressByCoordKey?: Map<string, number>,
): HexCoord | null {
	return resolveCoordFromPathPosition(
		{ pathProgress, branchCorridorId: null },
		pathCoordSet,
		startCell,
		new Map(),
		pathProgressByCoordKey,
	);
}

export function resolveFurthestUnlockedPathProgress(
	pathCells: PathCell[],
	unlockedStopIds: Set<string>,
	pathProgressByCoordKey: Map<string, number>,
): number {
	let maxProgress = 0;
	for (const cell of pathCells) {
		if (!cell.nodeId?.startsWith("sq:map:stop:")) {
			continue;
		}
		const stopId = cell.nodeId.replace("sq:map:stop:", "");
		if (!unlockedStopIds.has(stopId)) {
			continue;
		}
		const progress = pathProgressByCoordKey.get(
			coordKey(cell.row, cell.col),
		);
		if (progress != null) {
			maxProgress = Math.max(maxProgress, progress);
		}
	}
	return maxProgress;
}

export interface FurthestUnlockedStopOnPath {
	stopId: string;
	row: number;
	col: number;
	pathProgress: number;
}

/** Furthest unlocked stop tile along the path (by path progress, then path index). */
export function resolveFurthestUnlockedStopOnPath(
	pathCells: PathCell[],
	unlockedStopIds: Set<string>,
	pathProgressByCoordKey: Map<string, number>,
): FurthestUnlockedStopOnPath | null {
	let best: FurthestUnlockedStopOnPath | null = null;
	let bestPathIndex = -1;
	for (const cell of pathCells) {
		if (!cell.nodeId?.startsWith("sq:map:stop:")) {
			continue;
		}
		const stopId = cell.nodeId.replace("sq:map:stop:", "");
		if (!unlockedStopIds.has(stopId)) {
			continue;
		}
		const progress = pathProgressByCoordKey.get(
			coordKey(cell.row, cell.col),
		);
		if (progress == null) {
			continue;
		}
		if (
			!best ||
			progress > best.pathProgress ||
			(progress === best.pathProgress && cell.pathIndex > bestPathIndex)
		) {
			best = {
				stopId,
				row: cell.row,
				col: cell.col,
				pathProgress: progress,
			};
			bestPathIndex = cell.pathIndex;
		}
	}
	return best;
}

/** @deprecated Use resolveFurthestUnlockedPathProgress with buildPathProgressByCoordKey. */
export function resolveFurthestUnlockedPathIndex(
	pathCells: PathCell[],
	unlockedStopIds: Set<string>,
): number {
	let maxIndex = 0;
	for (const cell of pathCells) {
		if (!cell.nodeId?.startsWith("sq:map:stop:")) {
			continue;
		}
		const stopId = cell.nodeId.replace("sq:map:stop:", "");
		if (unlockedStopIds.has(stopId)) {
			maxIndex = Math.max(maxIndex, cell.pathIndex);
		}
	}
	return maxIndex;
}

export function findPathTileRoute(
	from: HexCoord,
	to: HexCoord,
	pathCoordSet: Set<string>,
): HexCoord[] | null {
	const fromKey = coordKey(from.row, from.col);
	const toKey = coordKey(to.row, to.col);
	if (fromKey === toKey) {
		return [from];
	}
	if (!pathCoordSet.has(fromKey) || !pathCoordSet.has(toKey)) {
		return null;
	}

	const queue: HexCoord[] = [from];
	const cameFrom = new Map<string, string | null>();
	cameFrom.set(fromKey, null);

	while (queue.length > 0) {
		const current = queue.shift();
		if (!current) {
			continue;
		}
		const currentKey = coordKey(current.row, current.col);
		if (currentKey === toKey) {
			const route: HexCoord[] = [];
			let key: string | null = toKey;
			while (key) {
				route.push(parseCoordKey(key));
				key = cameFrom.get(key) ?? null;
			}
			route.reverse();
			return route;
		}

		for (const neighbor of getNeighbors(current.row, current.col)) {
			const neighborKey = coordKey(neighbor.row, neighbor.col);
			if (!pathCoordSet.has(neighborKey) || cameFrom.has(neighborKey)) {
				continue;
			}
			cameFrom.set(neighborKey, currentKey);
			queue.push(neighbor);
		}
	}

	return null;
}

export function isPathTileTraversalEnabled(params: {
	row: number;
	col: number;
	pathProgress: number | undefined;
	pathCoordSet: Set<string>;
	furthestUnlockedPathProgress: number;
	isPathIndexVisible: (pathIndex: number) => boolean;
	pathIndexForVisibility?: number;
}): boolean {
	const key = coordKey(params.row, params.col);
	if (!params.pathCoordSet.has(key)) {
		return false;
	}
	if (params.pathProgress == null) {
		return false;
	}
	if (params.pathProgress > params.furthestUnlockedPathProgress) {
		return false;
	}
	if (
		params.pathIndexForVisibility != null &&
		params.pathIndexForVisibility >= 0 &&
		!params.isPathIndexVisible(params.pathIndexForVisibility)
	) {
		return false;
	}
	return true;
}

export function isPathRouteTraversalEnabled(params: {
	route: HexCoord[];
	pathCoordSet: Set<string>;
	pathProgressByCoordKey: Map<string, number>;
	pathIndexByCoordKey: Map<string, number>;
	furthestUnlockedPathProgress: number;
	isPathIndexVisible: (pathIndex: number) => boolean;
}): boolean {
	for (const step of params.route) {
		const key = coordKey(step.row, step.col);
		if (
			!isPathTileTraversalEnabled({
				row: step.row,
				col: step.col,
				pathProgress: params.pathProgressByCoordKey.get(key),
				pathCoordSet: params.pathCoordSet,
				furthestUnlockedPathProgress:
					params.furthestUnlockedPathProgress,
				isPathIndexVisible: params.isPathIndexVisible,
				pathIndexForVisibility: params.pathIndexByCoordKey.get(key),
			})
		) {
			return false;
		}
	}
	return true;
}

export interface PathFocusStopItem {
	pathIndex: number;
	stopId?: string;
}

export interface PathFocusAdjacentStopIds {
	prevStopId: string | null;
	nextStopId: string | null;
}

export interface ScopedPanelNavAvailability extends PathFocusAdjacentStopIds {
	canGoPrev: boolean;
	canGoNext: boolean;
}

/** Immediate content-stop neighbours along map path focus order. */
export function resolvePathFocusAdjacentStopIds(
	pathFocusOrder: PathFocusStopItem[],
	anchorStopId: string,
): PathFocusAdjacentStopIds {
	const contentStopIds = pathFocusOrder
		.map((item) => item.stopId)
		.filter((stopId): stopId is string => Boolean(stopId));
	const anchorIndex = contentStopIds.indexOf(anchorStopId);
	if (anchorIndex < 0) {
		return { prevStopId: null, nextStopId: null };
	}
	return {
		prevStopId: contentStopIds[anchorIndex - 1] ?? null,
		nextStopId: contentStopIds[anchorIndex + 1] ?? null,
	};
}

export function canTraverseMapRouteToStop(params: {
	originCoord: HexCoord | null;
	targetStopId: string;
	graph: ModuleMapGraph;
	layout: Pick<MapLayout, "pathCells" | "pathCoordSet" | "nodeById">;
	pathCoordSet: Set<string>;
	pathProgressByCoordKey: Map<string, number>;
	pathIndexByCoordKey: Map<string, number>;
	furthestUnlockedPathProgress: number;
	isPathIndexVisible: (pathIndex: number) => boolean;
	unlockedStopIds: Set<string>;
	resolveTargetCell: (
		stopId: string,
		graph: ModuleMapGraph,
		layout: Pick<MapLayout, "pathCells" | "pathCoordSet" | "nodeById">,
	) => PathCell | null;
}): boolean {
	if (
		!params.originCoord ||
		!params.unlockedStopIds.has(params.targetStopId)
	) {
		return false;
	}
	const targetCell = params.resolveTargetCell(
		params.targetStopId,
		params.graph,
		params.layout,
	);
	if (!targetCell) {
		return false;
	}
	const route = findPathTileRoute(
		params.originCoord,
		{ row: targetCell.row, col: targetCell.col },
		params.pathCoordSet,
	);
	if (!route?.length) {
		return false;
	}
	if (route.length === 1) {
		return true;
	}
	return isPathRouteTraversalEnabled({
		route,
		pathCoordSet: params.pathCoordSet,
		pathProgressByCoordKey: params.pathProgressByCoordKey,
		pathIndexByCoordKey: params.pathIndexByCoordKey,
		furthestUnlockedPathProgress: params.furthestUnlockedPathProgress,
		isPathIndexVisible: params.isPathIndexVisible,
	});
}

export function resolveScopedPanelNavAvailability(params: {
	anchorStopId: string;
	pathFocusOrder: PathFocusStopItem[];
	originCoord: HexCoord | null;
	graph: ModuleMapGraph;
	layout: Pick<MapLayout, "pathCells" | "pathCoordSet" | "nodeById">;
	pathCoordSet: Set<string>;
	pathProgressByCoordKey: Map<string, number>;
	pathIndexByCoordKey: Map<string, number>;
	furthestUnlockedPathProgress: number;
	isPathIndexVisible: (pathIndex: number) => boolean;
	unlockedStopIds: Set<string>;
	resolveTargetCell: (
		stopId: string,
		graph: ModuleMapGraph,
		layout: Pick<MapLayout, "pathCells" | "pathCoordSet" | "nodeById">,
	) => PathCell | null;
}): ScopedPanelNavAvailability {
	const { prevStopId, nextStopId } = resolvePathFocusAdjacentStopIds(
		params.pathFocusOrder,
		params.anchorStopId,
	);
	const canTraverse = (stopId: string | null) =>
		stopId != null &&
		canTraverseMapRouteToStop({
			originCoord: params.originCoord,
			targetStopId: stopId,
			graph: params.graph,
			layout: params.layout,
			pathCoordSet: params.pathCoordSet,
			pathProgressByCoordKey: params.pathProgressByCoordKey,
			pathIndexByCoordKey: params.pathIndexByCoordKey,
			furthestUnlockedPathProgress: params.furthestUnlockedPathProgress,
			isPathIndexVisible: params.isPathIndexVisible,
			unlockedStopIds: params.unlockedStopIds,
			resolveTargetCell: params.resolveTargetCell,
		});
	return {
		prevStopId,
		nextStopId,
		canGoPrev: canTraverse(prevStopId),
		canGoNext: canTraverse(nextStopId),
	};
}

export function resolveCharacterStepDurationMs(
	msPerTile: number = DEFAULT_CHARACTER_MOVEMENT_MS_PER_TILE,
): number {
	return Math.max(16, Math.round(msPerTile));
}

export function resolveNextPathCoord(
	from: HexCoord,
	pathCoordSet: Set<string>,
	pathProgressByCoordKey: Map<string, number>,
): HexCoord | null {
	const fromProgress = pathProgressByCoordKey.get(
		coordKey(from.row, from.col),
	);
	if (fromProgress == null) {
		return null;
	}

	let nextCoord: HexCoord | null = null;
	let nextProgress = Number.POSITIVE_INFINITY;
	for (const neighbor of getNeighbors(from.row, from.col)) {
		const neighborKey = coordKey(neighbor.row, neighbor.col);
		if (!pathCoordSet.has(neighborKey)) {
			continue;
		}
		const neighborProgress = pathProgressByCoordKey.get(neighborKey);
		if (
			neighborProgress == null ||
			neighborProgress <= fromProgress ||
			neighborProgress >= nextProgress
		) {
			continue;
		}
		nextProgress = neighborProgress;
		nextCoord = neighbor;
	}
	return nextCoord;
}

export function resolvePlayerDirectionBetween(
	from: HexCoord,
	to: HexCoord,
): HexDirection {
	return getDirection(from.row, from.col, to.row, to.col);
}

/**
 * Radians for hex facing; 0 = screen-up / north (same convention as scene actors).
 * Sector centers: upRight=π/6 … upLeft=11π/6.
 */
export function facingRadForHexDirection(direction: HexDirection): number {
	switch (direction) {
		case "upRight":
			return Math.PI / 6;
		case "right":
			return Math.PI / 2;
		case "downRight":
			return (5 * Math.PI) / 6;
		case "downLeft":
			return (7 * Math.PI) / 6;
		case "left":
			return (3 * Math.PI) / 2;
		case "upLeft":
			return (11 * Math.PI) / 6;
	}
}

export function resolveFacingForStep(
	from: HexCoord,
	to: HexCoord,
): {
	facingDirection: HexDirection;
	facingRad: number;
} {
	const facingDirection = resolvePlayerDirectionBetween(from, to);
	return {
		facingDirection,
		facingRad: facingRadForHexDirection(facingDirection),
	};
}

export function resolvePlayerIconForStep(
	from: HexCoord,
	to: HexCoord,
	playerIcons: PathCharacterPlayerIcons,
): string {
	const direction = resolvePlayerDirectionBetween(from, to);
	return playerIcons[direction] ?? playerIcons.right;
}

/** Any coord in the given hex direction from `origin` (for facing when blocked). */
export function resolvePseudoTargetForHexDirection(
	origin: HexCoord,
	hexDirection: HexDirection,
): HexCoord {
	for (const neighbor of getNeighbors(origin.row, origin.col)) {
		if (
			getDirection(origin.row, origin.col, neighbor.row, neighbor.col) ===
			hexDirection
		) {
			return neighbor;
		}
	}
	return origin;
}

/** Nearest path tile center to a display-space point. */
export function resolveNearestPathTileFromDisplayCenter(
	displayCenter: { left: number; top: number },
	pathCoordSet: Set<string>,
	metrics: HexagonLayoutMetrics,
): HexCoord | null {
	let nearestCoord: HexCoord | null = null;
	let nearestDistance = Number.POSITIVE_INFINITY;

	for (const key of pathCoordSet) {
		const coord = parseCoordKey(key);
		const tileCenter = hexCellCenter(coord.row, coord.col, metrics);
		const distance = Math.hypot(
			tileCenter.left - displayCenter.left,
			tileCenter.top - displayCenter.top,
		);
		if (distance < nearestDistance) {
			nearestDistance = distance;
			nearestCoord = coord;
		}
	}

	return nearestCoord;
}

/**
 * Pathfinding origin that is guaranteed to be in `pathCoordSet` when possible.
 * Off-path candidates (stale hold after display-frame shift, remount races) are
 * snapped to the nearest path tile by display center; otherwise `fallback`.
 */
export function ensureOnPathCoord(
	candidate: HexCoord | null | undefined,
	pathCoordSet: Set<string>,
	metrics: HexagonLayoutMetrics,
	fallback: HexCoord | null = null,
): HexCoord | null {
	if (
		candidate != null &&
		pathCoordSet.has(coordKey(candidate.row, candidate.col))
	) {
		return candidate;
	}
	if (candidate != null) {
		const snapped = resolveNearestPathTileFromDisplayCenter(
			hexCellCenter(candidate.row, candidate.col, metrics),
			pathCoordSet,
			metrics,
		);
		if (snapped != null) {
			return snapped;
		}
	}
	if (
		fallback != null &&
		pathCoordSet.has(coordKey(fallback.row, fallback.col))
	) {
		return fallback;
	}
	if (fallback != null) {
		return (
			resolveNearestPathTileFromDisplayCenter(
				hexCellCenter(fallback.row, fallback.col, metrics),
				pathCoordSet,
				metrics,
			) ?? fallback
		);
	}
	return null;
}

/** True when `candidate` is missing from the path set (stale absolute grid). */
export function isOffPathCoord(
	candidate: HexCoord | null | undefined,
	pathCoordSet: Set<string>,
): boolean {
	if (candidate == null) {
		return false;
	}
	return !pathCoordSet.has(coordKey(candidate.row, candidate.col));
}

const STOP_NODE_ID_PREFIX = "sq:map:stop:";

/**
 * After a layout seed / path refresh, pick a valid on-path coord near the
 * player's last display position. Prefers the nearest stop tile; falls back to
 * the nearest path tile, then start / first path cell.
 */
export function resolveRestoredPlayerPathCoordAfterLayoutChange(input: {
	displayCenter: { left: number; top: number } | null;
	layout: Pick<MapLayout, "pathCells" | "pathCoordSet" | "startCell">;
	metrics: HexagonLayoutMetrics;
}): HexCoord | null {
	const { displayCenter, layout, metrics } = input;
	const firstPathCell = layout.pathCells[0];
	const startKey = coordKey(layout.startCell.row, layout.startCell.col);
	const fallbackCoord = layout.pathCoordSet.has(startKey)
		? layout.startCell
		: firstPathCell
			? { row: firstPathCell.row, col: firstPathCell.col }
			: null;

	if (displayCenter == null) {
		return fallbackCoord;
	}

	let nearestStop: HexCoord | null = null;
	let nearestStopDistance = Number.POSITIVE_INFINITY;
	for (const cell of layout.pathCells) {
		if (!cell.nodeId?.startsWith(STOP_NODE_ID_PREFIX)) {
			continue;
		}
		const key = coordKey(cell.row, cell.col);
		if (!layout.pathCoordSet.has(key)) {
			continue;
		}
		const tileCenter = hexCellCenter(cell.row, cell.col, metrics);
		const distance = Math.hypot(
			tileCenter.left - displayCenter.left,
			tileCenter.top - displayCenter.top,
		);
		if (distance < nearestStopDistance) {
			nearestStopDistance = distance;
			nearestStop = { row: cell.row, col: cell.col };
		}
	}
	if (nearestStop) {
		return nearestStop;
	}

	return (
		resolveNearestPathTileFromDisplayCenter(
			displayCenter,
			layout.pathCoordSet,
			metrics,
		) ?? fallbackCoord
	);
}
